import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';

import { getStateHeroImages } from 'config/stateHeroImages';
import { Picture } from 'components/core/Picture';
import { H1, Paragraph } from 'components/core/Typography';
import ServerPreload from 'components/core/ServerPreload';
import { SearchBox } from 'components/search/SearchBox';
import MapPin from 'components/svg/MapPin';
import useSearchHandler from 'hooks/useSearchHandler';

import './StateHero.scss';

// Must stay identical to the <picture> sources' media queries below - a
// mismatch means the browser preloads a size it never renders.
const MOBILE_MEDIA = '(max-width: 767.99px)';
const DESKTOP_MEDIA = '(min-width: 768px)';

function StateHero({
	location,
	stateNickname,
	className,
	searchType,
	additionalSearchParams,
	type,
}) {
	const { state, fullState } = location;
	const images = useMemo(() => getStateHeroImages(state), [state]);
	const altText = `${fullState} - ${stateNickname}`;
	const hasImages = images !== null;

	const handleSearch = useSearchHandler({
		searchType,
		additionalSearchParams,
		type,
		location,
	});

	return (
		<div className={className ? `state-hero ${className}` : 'state-hero'}>
			{hasImages ? (
				<>
					{/* Server-side preload declarations - collected at render time, injected in head
					 * crossOrigin="anonymous" ensures preload and img fetch share the same cache.
					 * DEPENDENCY: CDN must return Access-Control-Allow-Origin headers for this to work.
					 * If CORS headers are missing, preload will be blocked or image may fail to load. */}
					<ServerPreload
						href={images.mobile}
						media={MOBILE_MEDIA}
						crossOrigin="anonymous"
					/>
					<ServerPreload
						href={images.desktop}
						media={DESKTOP_MEDIA}
						crossOrigin="anonymous"
					/>
				</>
			) : null}
			<div className="state-hero__wrapper">
				<div
					className={
						hasImages
							? 'state-hero__image-container'
							: 'state-hero__image-container state-hero__image-container--gradient'
					}
				>
					{hasImages ? (
						<Picture
							src={images.desktop}
							alt={altText}
							className="state-hero__image"
							pictureClassName="state-hero__picture"
							sources={[
								{
									media: MOBILE_MEDIA,
									srcSet: images.mobile,
									type: 'image/webp',
								},
								{
									media: DESKTOP_MEDIA,
									srcSet: images.desktop,
									type: 'image/webp',
								},
							]}
							loading="eager"
							fetchPriority="high"
							crossOrigin="anonymous"
						/>
					) : null}
				</div>

				<div className="state-hero__card">
					<H1 className="state-hero__headline">
						Find Self Storage in <br />
						{fullState}
					</H1>

					<Paragraph className="state-hero__subheading">
						Make a free reservation in minutes!
					</Paragraph>
					<SearchBox
						onSearch={handleSearch}
						icon={<MapPin fill="#146FB4" />}
						id="state-hero-search"
						inputLabel="Enter ZIP Code or City Name"
						useBrowserLocation
						ariaLabel={`Search for storage in ${fullState}`}
					/>
				</div>
			</div>
		</div>
	);
}

StateHero.propTypes = {
	location: PropTypes.shape({
		state: PropTypes.string.isRequired,
		fullState: PropTypes.string.isRequired,
	}).isRequired,
	stateNickname: PropTypes.string.isRequired,
	className: PropTypes.string,
	searchType: PropTypes.string,
	additionalSearchParams: PropTypes.object,
	type: PropTypes.string,
};

export { StateHero as StateHeroComponent };

export default memo(StateHero);
